import React from 'react';
import {
  BrowserRouter as Router,
  Switch,
} from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { NotificationContainer } from 'react-notifications';
import { createMuiTheme, responsiveFontSizes, MuiThemeProvider } from '@material-ui/core';
import moment from 'moment';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import HomePage from './pages/home/home';
import RegisterPage from './pages/register/register';
import RegistrationSuccessPage from './pages/registrationSuccess/registrationSuccess';
import Error404Page from './pages/404/404';
import LoginPage from './pages/login/login';
import ResetPasswordRequestPage from './pages/resetPasswordRequest/resetPasswordRequest';
import ResetPasswordPage from './pages/resetPassword/resetPassword';
import CreateProductPage from './pages/createProduct/createProduct';
import CreateOrderPage from './pages/createOrder/createOrder';
import CreateOrderSuccessPage from './pages/createOrderSuccess/createOrderSuccess';
import OrdersListPage from './pages/ordersList/ordersList';
import MyOrdersPage from './pages/myOrders/myOrders';
import PaymentFinishedPage from './pages/paymentFinished/paymentFinished';
import appStateActions from './actions/appState/appState';
import authActions from './actions/auth/auth';
import Route from './components/route/route';
import OfflineModeOverlay from './components/offlineModeOverlay/offlineModeOverlay';
import connectivity from './services/connectivity';
import 'moment/locale/pl';

moment.locale('pl');

const theme = responsiveFontSizes(createMuiTheme({
  palette: {
    primary: {
      main: '#184655',
    },
  },
}));

class App extends React.Component {
  constructor(props) {
    super(props);

    this.initApp();
  }

  initApp = async () => {
    const {
      initApp, setIsOnline, logOut, isLoggedIn,
    } = this.props;

    await connectivity.networkingCheckTask({
      onNetworkStateChange: setIsOnline,
      onTokenError: logOut,
      getIsLoggedIn: () => isLoggedIn,
    })();

    initApp();
  }

  render() {
    const { isAppInitialized } = this.props;

    if (!isAppInitialized) {
      return null;
    }

    return (
      <MuiThemeProvider theme={theme}>
        <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale="pl">
          <Router>
            <Switch>
              <Route path="/" exact>
                <HomePage />
              </Route>
              <Route path="/register" exact>
                <RegisterPage />
              </Route>
              <Route path="/registration-success" exact>
                <RegistrationSuccessPage />
              </Route>
              <Route path="/log-in" exact>
                <LoginPage />
              </Route>
              <Route path="/reset-password-request" exact>
                <ResetPasswordRequestPage />
              </Route>
              <Route path="/reset-password" exact>
                <ResetPasswordPage />
              </Route>
              <Route path="/manage-products" exact authorizedOnly allowedRoles={['Admin']}>
                <CreateProductPage />
              </Route>
              <Route path="/orders-list" key="orders-list" exact authorizedOnly allowedRoles={['Admin']}>
                <OrdersListPage realized={false} />
              </Route>
              <Route path="/realized-orders-list" key="realized-orders-list" exact authorizedOnly allowedRoles={['Admin']}>
                <OrdersListPage realized />
              </Route>
              <Route path="/create-order" exact authorizedOnly>
                <CreateOrderPage />
              </Route>
              <Route path="/create-order-success" exact authorizedOnly>
                <CreateOrderSuccessPage />
              </Route>
              <Route path="/my-orders" exact authorizedOnly>
                <MyOrdersPage />
              </Route>
              <Route path="/payment-finished/success" exact authorizedOnly>
                <PaymentFinishedPage success />
              </Route>
              <Route path="/payment-finished/failure" exact authorizedOnly>
                <PaymentFinishedPage success={false} />
              </Route>
              <Route>
                <Error404Page />
              </Route>
            </Switch>
          </Router>

          <OfflineModeOverlay />
          <NotificationContainer />
        </MuiPickersUtilsProvider>
      </MuiThemeProvider>
    );
  }
}

App.propTypes = {
  initApp: PropTypes.func.isRequired,
  logOut: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  isAppInitialized: PropTypes.bool.isRequired,
  setIsOnline: PropTypes.func.isRequired,
};

const mapState = state => ({
  isLoggedIn: !!state.auth.token,
  isAppInitialized: state.appState.isAppInitialized,
});

const bindActions = {
  initApp: appStateActions.initApp,
  setIsOnline: appStateActions.setIsOnline,
  logOut: authActions.logOut,
};

export default connect(mapState, bindActions)(App);
